// THIS IS OBSOLETE DO NOT USE !

// THIS IS OBSOLETE DO NOT USE !

// THIS IS OBSOLETE DO NOT USE !

import { useCallback, useRef, useState, useEffect } from 'react';
import userSettingsEngine from '../controllers/UserSettings/UserSettingsEngine';

// Minimum px of the panel's top-left corner that stays on-screen — keeps the
// drag handle (titlebar) grabbable even if the panel is dragged toward an edge.
const MIN_VISIBLE = 40;

/**
 * Clamps a position to the viewport — left/top can never go negative, and are
 * capped so the top-left corner stays within MIN_VISIBLE px of the right/bottom edge.
 * @param {{ top: number, left: number }} pos
 * @returns {{ top: number, left: number }}
 */
const _clamp = (pos) => ({
    left: Math.max(0, Math.min(pos.left, window.innerWidth - MIN_VISIBLE)),
    top: Math.max(0, Math.min(pos.top, window.innerHeight - MIN_VISIBLE)),
});

/**
 * Reads a persisted panel position for the given name.
 * @param {string|null} name
 * @returns {{ top: number, left: number } | null}
 */
const _readPersistedPos = (name) => {
    if (!name) return null;
    try {
        const raw = userSettingsEngine.resolve(`dialogPos.${name}`);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed?.top !== 'number' || typeof parsed?.left !== 'number') return null;
        return parsed;
    } catch {
        return null;
    }
};

/**
 * Persists a panel position for the given name — fire and forget.
 * @param {string} name
 * @param {{ top: number, left: number }} pos
 */
const _persistPos = (name, pos) => {
    const value = JSON.stringify(pos);
    userSettingsEngine.set(`dialogPos.${name}`, value);
};

/**
 * useDraggablePanel — reusable drag logic for fixed overlay panels.
 * Returns current position and props to spread on the drag handle element.
 * Position is clamped to the viewport on init/mount (e.g. a persisted
 * posX/posY that's now off-screen after a viewport resize) and during drag —
 * left/top can never go negative, and are capped so the top-left corner stays
 * within MIN_VISIBLE px of the right/bottom edge.
 *
 * If `name` is provided, the panel's position is persisted to user settings
 * under key `dialogPos.{name}` and restored on next open. Pass null/omit to
 * opt out — purely local, non-persisted drag behavior (unchanged default).
 *
 * @param {string|null} name - Unique panel identifier for position persistence, or null to skip
 * @param {object} initialPos - { top: number, left: number } fallback position if nothing persisted
 * @param {function} [onDragEnd] - optional callback(pos) called when drag ends
 * @returns {{ pos, setPos, dragHandleProps }}
 *
 * Usage (non-persisted, unchanged):
 *   const { pos, dragHandleProps } = useDraggablePanel(null, { top: 0, left: window.innerWidth / 2 - 260 });
 *
 * Usage (persisted):
 *   const { pos, dragHandleProps } = useDraggablePanel('TopicGroupSearchDialog', DEFAULT_POS());
 */
export default function useDraggablePanel(name, initialPos = { top: 0, left: 0 }, onDragEnd) {
    const [pos, setPos] = useState(() => _clamp(_readPersistedPos(name) ?? initialPos));
    const [dragging, setDragging] = useState(false);
    const offset = useRef({ x: 0, y: 0 });
    const posRef = useRef(pos);

    // Re-clamp on mount — covers viewport resizes between sessions where a
    // persisted position (posX/posY) is no longer on-screen.
    useEffect(() => {
        setPos((current) => _clamp(current));
    }, []);

    const onMouseDown = useCallback(
        (e) => {
            // Only drag on left button — ignore clicks on buttons inside titlebar
            if (e.button !== 0) return;
            if (e.target.closest('button')) return;

            // Prevent text selection on the page while dragging
            e.preventDefault();

            offset.current = { x: e.clientX - pos.left, y: e.clientY - pos.top };
            setDragging(true);

            const onMouseMove = (ev) => {
                const next = _clamp({
                    left: ev.clientX - offset.current.x,
                    top: ev.clientY - offset.current.y,
                });
                posRef.current = next;
                setPos(next);
            };

            const onMouseUp = () => {
                setDragging(false);
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
                if (name) _persistPos(name, posRef.current);
                onDragEnd?.(posRef.current);
            };

            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        },
        [pos, name, onDragEnd],
    );

    const dragHandleProps = {
        onMouseDown,
        style: { cursor: dragging ? 'grabbing' : 'grab' },
    };

    return { pos, setPos, dragHandleProps };
}
